import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $userStore } from '@lib/store';
import { cn, ValidateUtils } from '@shared/utils';
import { ProfileResponse } from '@shared/api';
import { BaseInput, Button, Modal, PhoneInput } from '@shared/widgets';
import { Constants } from '@shared/constants';

interface FormData {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
}

interface EditDetailsModalProps {
  show: boolean;
  onClose: () => void;
  user: ProfileResponse;
}

export const EditDetailsModal = (props: EditDetailsModalProps) => {
  const userStore = useUnit($userStore.store);

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    },
  });
  const { t } = useTranslation('user');

  useEffect(() => {
    if (!props.show) return;
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  }, [props.show]);

  const closeHandler = () => {
    props.onClose();
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  };
  const submitHandler = async (data: FormData) => {
    await $userStore.events.updateUser({
      id: props.user.id,
      firstName: data.firstName,
      lastName: data.lastName,
      userType: props.user.userType,
      position: data.position,
      phoneNumber: data.phone.phoneNumber,
      phoneAreaCode: data.phone.countryCode,
      emailAddress: data.emailAddress,
    });
    props.onClose();
  };

  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.editDetails.title')}>
      <form className={cn('flex w-[640px] flex-col gap-4', 'max-md:w-[308px] max-md:gap-2')} noValidate onSubmit={handleSubmit(submitHandler)}>
        <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
          <Controller
            name='userName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              minLength: { value: 6, message: t('modals.add.minimumError') },
              maxLength: { value: 30, message: t('errors.form.maxCharacters', { ns: 'common', max: 30 }) },
              pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('placeholders.username', { ns: 'common' })}
                placeholder={t('placeholders.username', { ns: 'common' })}
                required
                readOnly
                error={error?.message}
              />
            )}
          />
          <Controller
            name='firstName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('placeholders.firstName', { ns: 'common' })}
                placeholder={t('placeholders.firstName', { ns: 'common' })}
                required
                error={error?.message}
              />
            )}
          />
        </div>
        <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
          <Controller
            name='lastName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('placeholders.lastName', { ns: 'common' })}
                placeholder={t('placeholders.lastName', { ns: 'common' })}
                required
                error={error?.message}
              />
            )}
          />
          <Controller
            name='emailAddress'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('placeholders.email', { ns: 'common' })}
                type='email'
                placeholder={t('placeholders.email', { ns: 'common' })}
                required
                error={error?.message}
              />
            )}
          />
        </div>
        <div className={cn('flex gap-4', 'max-md:flex-col max-md:gap-2')}>
          <Controller
            name='position'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              maxLength: { value: 64, message: t('errors.form.maxCharacters', { ns: 'common', max: 64 }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('placeholders.positionRole', { ns: 'common' })}
                placeholder={t('placeholders.positionRole', { ns: 'common' })}
                required
                error={error?.message}
              />
            )}
          />
          <Controller
            name='phone'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              validate: (value) => {
                if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
                if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
                if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput label='Phone' enableSearch required value={field.value} onChange={field.onChange} error={error?.message} />
            )}
          />
        </div>
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting || userStore.loading}>
          {t('buttons.saveChanges', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
